<template>
    <div class="card mb-3">
        <div class="card-body">
            <div class="d-flex align-items-center">
                <div class="custom-control custom-switch mr-3">
                    <input
                        :id="'trigger-active-' + uid"
                        type="checkbox"
                        class="custom-control-input"
                        :checked="trigger.is_active"
                        @change="on_toggle_active($event.target.checked)"
                    >
                    <label class="custom-control-label" :for="'trigger-active-' + uid"></label>
                </div>

                <div class="flex-grow-1" style="cursor: pointer;" @click="is_expanded = !is_expanded">
                    <span class="font-weight-bold">{{ status_title }}</span>
                    <span class="badge badge-pill ml-2" :class="badge_class">{{ badge_text }}</span>
                </div>

                <button class="btn btn-icon btn-sm" @click="duplicate" v-if="trigger.id" :title="__t('DUPLICATE')">
                    <i class="fal fa-copy"></i>
                </button>

                <button class="btn btn-icon btn-sm text-danger" @click="remove" :title="__t('REMOVE')">
                    <i class="fal fa-trash-alt"></i>
                </button>

                <button class="btn btn-icon btn-sm" @click="is_expanded = !is_expanded" :title="__t('EXPAND')">
                    <i class="fal" :class="is_expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </button>
            </div>

            <div v-if="!is_expanded && trigger.message" class="text-muted small mt-2 text-truncate">
                {{ trigger.message }}
            </div>

            <div v-if="is_expanded" class="row mt-3">
                <div class="col-md-4">
                    <div class="form-group">
                        <label class="form-label">{{__t('EVENT')}}</label>
                        <b-form-select v-model="trigger.trigger_status" :options="status_options"></b-form-select>
                    </div>

                    <div class="form-group">
                        <label class="form-label">{{__t('SEND_AFTER_MINUTES')}}</label>
                        <b-form-input type="number" min="0" v-model.number="trigger.delay_minutes"></b-form-input>
                    </div>
                </div>

                <div class="col-md-8">
                    <div class="card">
                        <div class="card-body">
                            <h5 class="frame-heading mb-3">{{__t('MESSAGE')}}</h5>

                            <div class="form-group">
                                <label class="form-label">{{__t('VARIABLES')}}</label>
                                <div>
                                    <span
                                        class="badge badge-pill badge-light border mr-2 mb-2 px-3 py-2"
                                        style="cursor: pointer;"
                                        v-for="v in variables"
                                        :key="v"
                                        @click="insert_variable(v)"
                                    >{{v}}</span>
                                </div>
                            </div>

                            <div class="form-group mb-1">
                                <b-form-textarea
                                    ref="message_input"
                                    v-model="trigger.message"
                                    rows="4"
                                    max-rows="8"
                                ></b-form-textarea>
                            </div>
                            <small class="text-muted">{{ (trigger.message || '').length }} {{__t('CHARACTERS')}}</small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "order_notification_trigger",
    props : {
        rest : {
            type : Object
        },
        provider : {
            type : Object
        },
        trigger : {
            type : Object
        },
    },
    data() {
        return {
            is_expanded: !this.trigger.id,
            variables: ['{order_code}', '{rest_title}', '{rest_phone}', '{customer_name}','{review_url}'],
        }
    },
    created() {
        EventBus.$on('save_rest_called', this.save);
    },
    beforeDestroy() {
        EventBus.$off('save_rest_called', this.save);
    },
    computed: {
        uid() {
            return this.trigger.id || this.trigger._uid;
        },

        status_options() {
            return [
                {value: 'new', text: this.__t('ORDER_STATUS_NEW')},
                {value: 'accept', text: this.__t('ORDER_STATUS_ACCEPT')},
                {value: 'ready', text: this.__t('ORDER_STATUS_READY')},
                {value: 'close', text: this.__t('ORDER_STATUS_CLOSE')},
                {value: 'cancel', text: this.__t('ORDER_STATUS_CANCEL')},
            ];
        },

        status_title() {
            const found = this.status_options.find(o => o.value === this.trigger.trigger_status);
            return found ? found.text : this.trigger.trigger_status;
        },

        badge_class() {
            if (!this.trigger.is_active) {
                return 'badge-secondary';
            }
            return (parseInt(this.trigger.delay_minutes) > 0) ? 'badge-light border' : 'badge-success';
        },

        badge_text() {
            if (!this.trigger.is_active) {
                return this.__t('TRIGGER_DISABLED');
            }
            if (parseInt(this.trigger.delay_minutes) > 0) {
                return this.__t('TRIGGER_DELAYED') + ' ' + this.format_delay(this.trigger.delay_minutes);
            }
            return this.__t('TRIGGER_ACTIVE');
        },
    },
    methods : {
        insert_variable(variable) {
            const textarea = this.$refs.message_input.$el;
            const start = textarea.selectionStart || 0;
            const end = textarea.selectionEnd || 0;
            const current = this.trigger.message || '';

            this.trigger.message = current.slice(0, start) + variable + current.slice(end);

            this.$nextTick(() => {
                const pos = start + variable.length;
                textarea.focus();
                textarea.setSelectionRange(pos, pos);
            });
        },

        format_delay(minutes) {
            minutes = parseInt(minutes) || 0;
            if (minutes >= 60 && minutes % 60 === 0) {
                return (minutes / 60) + ' ' + this.__t('HOURS_SHORT');
            }
            return minutes + ' ' + this.__t('MINUTES_SHORT');
        },

        payload() {
            return {
                provider_type: this.provider.type,
                trigger_status: this.trigger.trigger_status,
                message: this.trigger.message || '',
                delay_minutes: parseInt(this.trigger.delay_minutes) || 0,
                is_active: !!this.trigger.is_active,
            };
        },

        on_toggle_active(value) {
            this.trigger.is_active = value;
        },

        save() {
            if (this.trigger.id) {
                this.patch_trigger(this.payload());
                return;
            }

            notification_api.post('/order_notification/restaurant/' + this.rest.id, this.payload())
                .then(({data}) => {
                    if (data.status === "operation_success") {
                        this.trigger.id = data.data.id;
                    }
                })
                .catch(({response}) => {
                    EventBus.$emit('show_toast', {err: response.data.message, type: "danger", title: response.data.status});
                });
        },

        patch_trigger(payload) {
            return notification_api.patch('/order_notification/restaurant/' + this.rest.id + '/' + this.trigger.id, payload)
                .catch(({response}) => {
                    EventBus.$emit('show_toast', {err: response.data.message, type: "danger", title: response.data.status});
                });
        },

        duplicate() {
            notification_api.post('/order_notification/restaurant/' + this.rest.id, this.payload())
                .then(({data}) => {
                    if (data.status === "operation_success") {
                        this.$emit('duplicated', data.data);
                    }
                })
                .catch(({response}) => {
                    EventBus.$emit('show_toast', {err: response.data.message, type: "danger", title: response.data.status});
                });
        },

        remove() {
            if (!this.trigger.id) {
                this.$emit('removed', this.trigger);
                return;
            }

            const is_ok = confirm_me(this.__t("ARE_SURE"));
            is_ok.then(() => {
                notification_api.delete('/order_notification/restaurant/' + this.rest.id + '/' + this.trigger.id)
                    .then(({data}) => {
                        if (data.status === "operation_success") {
                            this.$emit('removed', this.trigger);
                        }
                    })
                    .catch(({response}) => {
                        EventBus.$emit('show_toast', {err: response.data.message, type: "danger", title: response.data.status});
                    });
            });
        },
    },
}
</script>

<style scoped>

</style>
